'use client'

import { Check, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import { useState } from 'react'

import { cn } from '@/_lib/utils'

import { Button } from './button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

interface ComboboxOption {
  value: string
  label: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  placeholder: string
  value?: string
  notFoundMessage?: string
  onSelect: (...event: any[]) => void
  disabled?: boolean
}

export const Combobox = ({
  options,
  value,
  placeholder,
  notFoundMessage = 'Nenhuma opção encontrada.',
  onSelect,
  disabled,
}: ComboboxProps) => {
  const [open, setOpen] = useState(false)

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === value) {
      onSelect(null)
      setOpen(false)
      return
    }

    onSelect(selectedValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between overflow-hidden hover:bg-brand-green-primary-5"
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {value
              ? options.find(
                  (option) => option.value.toLowerCase() === value.toLowerCase()
                )?.label
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandEmpty>{notFoundMessage}</CommandEmpty>
          <CommandGroup className="scrollbar max-h-60 overflow-y-auto">
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={(value) => handleSelect(value)}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value?.toLowerCase() === option.value.toLowerCase()
                      ? 'opacity-100'
                      : 'opacity-0'
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
